import { useExamEventDetails } from '@exam-center/exam-event-management';
import {
  GenericResponseStatus,
  useExportExamEventExamineeScoreReportsLazyQuery,
  useGetExamEventExamineeScoreReportsTotalCountLazyQuery,
} from '@exam-center/graphql';
import { Button, FileUploadOutlinedIcon } from '@shared/components';
import { useDisclosure } from '@shared/hooks';
import { useSnackbar } from '@shared/snackbar';
import { useCallback, useMemo } from 'react';

import { ExportConfirmationModal } from '../../components';
import { useExamEventScoreReport } from '../../providers/exam-event-score-report-provider/use-exam-event-score-report';

export default function ExamEventScoreReportExportButton() {
  const { examEvent } = useExamEventDetails();
  const { scoreReports, selectedExamCourseId, selectedTagCategory, examCourseOptions, hasFilters } =
    useExamEventScoreReport();
  const [isOpen, { open, close }] = useDisclosure();
  const { showSnackbar } = useSnackbar();
  const [exportScoreReports, { loading: isExporting }] =
    useExportExamEventExamineeScoreReportsLazyQuery();
  const [fetchTotalCount, { data: totalCountData, loading: isTotalLoading }] =
    useGetExamEventExamineeScoreReportsTotalCountLazyQuery();

  const totalRecords =
    totalCountData?.ecGetExamEventExamineeScoreReportsTotalCount.total ?? scoreReports.length;

  // Stable reference so the modal's reset effect isn't retriggered by a new
  // array literal on every re-render.
  const defaultCourseIds = useMemo(() => [selectedExamCourseId], [selectedExamCourseId]);

  // Refetch the aggregate total whenever the modal selection changes. The
  // select-all sentinel resolves to `null` here, so expand it to every course
  // id for the count query, which requires an explicit id list.
  const handleSelectionChange = useCallback(
    (examCourseIds: string[] | null) => {
      const resolvedIds = examCourseIds ?? examCourseOptions.map((option) => option.value);
      if (resolvedIds.length === 0) return;

      fetchTotalCount({
        variables: {
          getExamEventExamineeScoreReportsTotalCountInput: {
            examEventId: examEvent.id,
            examCourseIds: resolvedIds,
          },
        },
        fetchPolicy: 'network-only',
      });
    },
    [examEvent.id, examCourseOptions, fetchTotalCount],
  );

  const handleExport = async (examCourseIds: string[] | null) => {
    if (!selectedTagCategory || examCourseIds?.length === 0) return;

    await exportScoreReports({
      variables: {
        exportExamEventExamineeScoreReportsInput: {
          examEventId: examEvent.id,
          examCourseIds: examCourseIds,
          tagCategory: selectedTagCategory,
        },
      },
      fetchPolicy: 'network-only',
      onCompleted: (data) => {
        if (data.ecExportExamEventExamineeScoreReports.status === GenericResponseStatus.Success) {
          showSnackbar({
            message: 'ส่งออกข้อมูลเรียบร้อยแล้ว',
            variant: 'success',
          });
        } else {
          showSnackbar({
            message: 'ส่งออกข้อมูลไม่สำเร็จ กรุณาลองใหม่อีกครั้ง',
            variant: 'error',
          });
        }
        close();
      },
      onError: () => {
        showSnackbar({
          message: 'ส่งออกข้อมูลไม่สำเร็จ กรุณาลองใหม่อีกครั้ง',
          variant: 'error',
        });
        close();
      },
    });
  };

  const isDisabled = !hasFilters || scoreReports.length === 0;

  return (
    <>
      <Button
        startIcon={<FileUploadOutlinedIcon />}
        disabled={isDisabled}
        variant='outlined'
        color='secondary'
        onClick={open}
      >
        Export
      </Button>

      {selectedTagCategory && (
        <ExportConfirmationModal
          open={isOpen}
          onClose={close}
          onConfirm={handleExport}
          onSelectionChange={handleSelectionChange}
          examStartedAt={examEvent.examStartedAt}
          examEndedAt={examEvent.examEndedAt}
          examRound={examEvent.roundNumber}
          examType={examEvent.examType}
          category={selectedTagCategory}
          courseOptions={examCourseOptions}
          defaultCourseIds={defaultCourseIds}
          totalRecords={totalRecords}
          isTotalLoading={isTotalLoading}
          loading={isExporting}
        />
      )}
    </>
  );
}
